import { useCallback, useState } from "react";
import { Category } from "@common/types/category";
import { Entity } from "@common/types/entity";
import { useSingleBusinessEntity } from "@hooks/useSingleBusinessEntity";
import { useBulkActions } from "../TransactionsBulkActions/useBulkActions";

/**
 * Hook to handle business category updates with entity selection logic
 */
export function useBulkActionHandlers() {
    const bulkActions = useBulkActions();
    const singleBusinessEntity = useSingleBusinessEntity();
    const [switchingToBusinessCategory, setSwitchingToBusinessCategory] =
        useState<Category | null>(null);

    const onBusinessSelected = useCallback(
        async (entity: Entity) => {
            if (switchingToBusinessCategory) {
                await bulkActions.updateSelected({
                    update: "entity",
                    value: entity,
                });
                await bulkActions.updateSelected({
                    update: "categoryId",
                    value: switchingToBusinessCategory.id,
                });
                setSwitchingToBusinessCategory(null);
            }
        },
        [switchingToBusinessCategory, bulkActions],
    );

    const handleCategoryUpdate = useCallback(
        (category: Category) => {
            if (
                category.isBusiness &&
                bulkActions.selectedArray.some((t) => !t.isBusiness)
            ) {
                if (singleBusinessEntity) {
                    return onBusinessSelected(singleBusinessEntity);
                }
                setSwitchingToBusinessCategory(category);
                return;
            }
            bulkActions.updateSelected({
                update: "categoryId",
                value: category.id,
            });
        },
        [bulkActions, singleBusinessEntity, onBusinessSelected],
    );

    const hideBusinessSelection = useCallback(() => {
        setSwitchingToBusinessCategory(null);
    }, []);

    return {
        switchingToBusinessCategory,
        handleCategoryUpdate,
        onBusinessSelected,
        hideBusinessSelection,
    };
}
